import { shallow } from "zustand/shallow";

import type {
    AssociatedFilters,
    FilterConfig,
    OptionConfig,
} from "../../config/types";
import { Translate } from "../../hooks/useTranslation";
import useExplorerStore from "../../store/store";
import { type Filter } from "../../store/types";
import Checkbox from "../Checkbox";
import { type Props as BaseProps } from "./CheckboxList";
import CheckboxListContainer from "./components/CheckboxListContainer";

type Value = OptionConfig["value"];
type Values = {
    prop: string;
    data: Value[];
};
type Props = {
    activeFilters: Filter[];
    filter: FilterConfig;
    onChange?: (filter: Filter) => void;
} & Pick<BaseProps<Value>, "isOpen">;

const CompositeFilterCheckboxList = ({
    activeFilters,
    filter,
    isOpen,
    onChange,
}: Props) => {
    const { filterHasFunds: hasFunds } = useExplorerStore(
        ({ filterHasFunds, search: { term } }) => ({ filterHasFunds, term }),
        shallow,
    );
    const getFlatActiveFilters = (prop: string) =>
        activeFilters.filter((f) => f.prop === prop).flatMap((f) => f.value);

    const handleChange = (
        values: Values,
        checked: boolean,
        associatedFilters: AssociatedFilters | undefined,
    ) => {
        const flatActiveFilters = getFlatActiveFilters(values.prop);
        if (!onChange) {
            return;
        }
        onChange({
            prop: values.prop,
            level: filter.level,
            value: checked
                ? values.data
                : flatActiveFilters.filter((x) => !values.data.includes(x)),
            type: filter.type,
            associatedFilters: associatedFilters,
        });
    };

    const isSelected = (prop: string, val: Value[]) => {
        const flatActiveFilters = getFlatActiveFilters(prop);
        const flatVals = val.flatMap((x) => x);
        return flatActiveFilters.some((x) => flatVals.includes(x as string));
    };

    const isDisabled = (option: OptionConfig | undefined) => {
        if (!option) {
            return true;
        }
        return hasFunds({
            level: filter.level,
            prop: option.prop!,
            value: [option.value],
            type: filter.type,
            associatedFilters: option.associatedFilters,
        });
    };

    return (
        <CheckboxListContainer
            containerClassName="py-2"
            isOpen={isOpen}
            title={filter.label}
            tooltip={{ content: filter.tooltip }}
        >
            {filter?.options?.map((item) => (
                <Checkbox<Values>
                    key={item.key}
                    label={<Translate>{item.label}</Translate>}
                    disabled={isDisabled(item)}
                    value={{
                        prop: item.prop as string,
                        data: Array.isArray(item.value)
                            ? item.value
                            : [item.value],
                    }}
                    selected={isSelected(item.prop as string, [item.value])}
                    onChange={(val, checked) =>
                        handleChange(val, checked, item.associatedFilters)
                    }
                    labelClassName="text-sm"
                />
            ))}
        </CheckboxListContainer>
    );
};

export default CompositeFilterCheckboxList;
